import {
  createKitRequestLineFormConstants,
  createKitRequestLinePartValidation,
  createKitRequestLineStockLotValidation,
} from 'views/kitting/Kit/components/constants';
import { useCreateKitRequestLinesMutation } from 'store/slices/clientV2/kitRequests';
import ExtendedTabs from 'ui-component/extended/ExtendedTabs';
import { useState } from 'react';
import * as Yup from 'yup';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { ExtendedDialog } from 'ui-component/extended/ExtendedDialog';
import { handleErr } from 'utils/functions';
import { ALL_APP_IDS } from 'constants/appConstants';
import { appPermissionAccessLevels } from 'types/apps';
import useSnackbar from 'hooks/useSnackbar';
import { ClientV2POSTRequest } from 'types/api';
import { KitRequestLinePOSTSchema } from 'types/kitting';
import CreateKitRequestFromPart from 'views/kitting/Kit/components/CreateUpdateKitRequestLineForms/CreateKitRequestFromPart';
import CreateUpdateKitRequestFromStockLot from 'views/kitting/Kit/components/CreateUpdateKitRequestLineForms/CreateUpdateKitRequestFromStockLot';
import { useKitRequestContext } from 'views/kitting/Kit/KitRequestContext';
import useGetCustomPropertyDefinitions from 'hooks/useGetCustomPropertyDefinitions';
import {
  CustomPropertyModel,
  CustomPropertyVisibility,
} from 'types/customProperty';
import _ from 'lodash';
import { useFeature } from '@growthbook/growthbook-react';
import { FeatureFlags } from 'types';

enum TAB_NAMES {
  PART = 'part',
  STOCK_LOT = 'stockLot',
}

type CreateKitRequestLineDialogProps = {
  dialogOpen: boolean;
  onClose: () => void;
  kitRequestId: string;
};

const CreateKitRequestLineDialog = ({
  dialogOpen,
  onClose,
  kitRequestId,
}: CreateKitRequestLineDialogProps) => {
  const tempUoM = useFeature(FeatureFlags.tempUoM).on;
  const { dispatchSuccessSnackbar } = useSnackbar();

  const [currentTab, setCurrentTab] = useState<TAB_NAMES>(TAB_NAMES.PART);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [disableCreateFromStockLot, setDisableCreateFromStockLot] =
    useState(false);

  const { kitRequest } = useKitRequestContext();

  const [createKitRequestLines, { isLoading: isLoadingCreateKitRequestLines }] =
    useCreateKitRequestLinesMutation();

  const {
    customProperties,
    getDefaultValuesForCustomProperties,
    customPropertiesFieldName,
  } = useGetCustomPropertyDefinitions({
    model: CustomPropertyModel.KIT_REQUEST_LINE,
    visibilityContext: CustomPropertyVisibility.NEW_RECORD_DIALOG,
  });

  const formDefaults = {
    [createKitRequestLineFormConstants.part.id]: '',
    [createKitRequestLineFormConstants.v2OrgPart.id]: '',
    [createKitRequestLineFormConstants.targetQuantityPart.id]: '',
    [createKitRequestLineFormConstants.stockLot.id]: '',
    [createKitRequestLineFormConstants.targetQuantityStockLot.id]: '',
    [customPropertiesFieldName]: getDefaultValuesForCustomProperties(),
  };

  const validationSchema =
    currentTab === TAB_NAMES.PART
      ? Yup.object().shape(createKitRequestLinePartValidation(tempUoM))
      : Yup.object().shape(createKitRequestLineStockLotValidation(tempUoM));

  const methods = useForm<FieldValues>({
    defaultValues: formDefaults,
    resolver: yupResolver(validationSchema),
    mode: 'all',
  });

  const {
    handleSubmit,
    reset,
    formState: { dirtyFields },
  } = methods;

  const handleClose = () => {
    setCurrentTab(TAB_NAMES.PART);
    onClose();
    setSubmitError(null);
    setDisableCreateFromStockLot(false);
    reset();
  };

  const getKitRequestLinePayload = (
    data: FieldValues
  ): ClientV2POSTRequest<KitRequestLinePOSTSchema> => {
    if (currentTab === TAB_NAMES.PART) {
      return {
        kitRequest: kitRequestId,
        v2OrgPart: data[createKitRequestLineFormConstants.v2OrgPart.id],
        ...(tempUoM
          ? {
              targetDimensionValue:
                data[
                  createKitRequestLineFormConstants.targetDimensionValuePart.id
                ],
              minimumDimensionValue:
                data[
                  createKitRequestLineFormConstants.targetDimensionValuePart.id
                ],
              dimensionUnit:
                data[createKitRequestLineFormConstants.dimensionUnitPart.id],
              dimensionClass:
                data[createKitRequestLineFormConstants.dimensionClassPart.id],
            }
          : {
              targetQuantity:
                data[createKitRequestLineFormConstants.targetQuantityPart.id],
              minimumQuantity:
                data[createKitRequestLineFormConstants.targetQuantityPart.id],
            }),
      };
    }
    return {
      kitRequest: kitRequestId,
      v2OrgPart:
        data[createKitRequestLineFormConstants.stockLot.id]?.v2OrgPart.id,
      locked: true,
      stockLotForAllocation:
        data[createKitRequestLineFormConstants.stockLot.id]?.id,
      ...(tempUoM
        ? {
            targetDimensionValue:
              data[
                createKitRequestLineFormConstants.targetDimensionValueStockLot
                  .id
              ],
            minimumDimensionValue:
              data[
                createKitRequestLineFormConstants.targetDimensionValueStockLot
                  .id
              ],
            dimensionUnit:
              data[createKitRequestLineFormConstants.dimensionUnitStockLot.id],
            dimensionClass:
              data[createKitRequestLineFormConstants.dimensionClassStockLot.id],
          }
        : {
            targetQuantity:
              data[createKitRequestLineFormConstants.targetQuantityStockLot.id],
            minimumQuantity:
              data[createKitRequestLineFormConstants.targetQuantityStockLot.id],
          }),
    };
  };

  const handleCreateKitRequestLineFromPart = async (data: FieldValues) => {
    try {
      const payload = getKitRequestLinePayload(data);
      if (Object.keys(dirtyFields).includes(customPropertiesFieldName)) {
        _.set(payload, 'customProperties', data[customPropertiesFieldName]);
      }
      const response = await createKitRequestLines(payload).unwrap();
      dispatchSuccessSnackbar('Kit Line created successfully');
      handleClose();
      return response;
    } catch (err) {
      handleErr(err, (errMessage: string) => {
        setSubmitError(errMessage);
      });
      return null;
    }
  };

  const onSubmit = async (data: FieldValues) => {
    await handleCreateKitRequestLineFromPart(data);
  };

  return (
    <ExtendedDialog
      onSubmit={handleSubmit(onSubmit)}
      formSubmitError={submitError}
      submitButtonCopy="Save"
      isSubmitting={isLoadingCreateKitRequestLines}
      open={dialogOpen}
      isForm
      onCloseDialog={handleClose}
      title="Create Kit Line"
      permissionScope={{
        app: ALL_APP_IDS.KITTING,
        accessLevel: appPermissionAccessLevels.edit,
      }}
      submitDisabled={Boolean(
        currentTab === TAB_NAMES.STOCK_LOT && disableCreateFromStockLot
      )}
    >
      <ExtendedTabs
        onChange={(tabName: string) => {
          setCurrentTab(tabName as TAB_NAMES);
        }}
        defaultTab={currentTab}
        tabOptions={[
          {
            name: TAB_NAMES.PART,
            label: 'Part',
            contents: (
              <FormProvider {...methods}>
                <CreateKitRequestFromPart customProperties={customProperties} />
              </FormProvider>
            ),
          },
          {
            disabled: (kitRequest?.sourceLocations ?? []).length === 0,
            name: TAB_NAMES.STOCK_LOT,
            label: 'Stock Lot',
            contents: (
              <FormProvider {...methods}>
                <CreateUpdateKitRequestFromStockLot
                  isCreate
                  customProperties={customProperties}
                />
              </FormProvider>
            ),
          },
        ]}
      />
    </ExtendedDialog>
  );
};

export default CreateKitRequestLineDialog;
